/** Server data the shell chrome needs: meta, backups, issuers, audit. */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** GET /api/v1/meta */
export interface AppMeta {
  readonly version: string;
  readonly dataFile: string;
  readonly dataDir: string;
  readonly lastBackupAt: string | null;
  readonly today: string;
  /** Earliest month with data (`YYYY-MM`); bounds the Finance year stepper. */
  readonly earliestPeriod?: string | null;
}

/** GET /api/v1/backups items, newest first. */
export interface BackupFile {
  readonly name: string;
  readonly createdAt: string;
  readonly kind: string;
  readonly size: number;
}

/** The fields of GET /api/v1/issuers items the shell uses. */
export interface IssuerOption {
  readonly id: string;
  readonly name: string;
  readonly shortCode?: string;
  readonly isArchived?: boolean;
}

/** GET /api/v1/audit items. */
export interface AuditEntry {
  readonly id: string;
  readonly at: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly action: string;
  readonly summary: string | null;
  readonly undoToken: string | null;
  /** v2: status label of a record-history entry («Счёт выставлен»), where it happened, who did it. */
  readonly label?: string | null;
  readonly where?: string | null;
  readonly by?: string | null;
}

/** A record whose history the Activity panel highlights (design `openActivity({key,title})`). */
export interface ActivityFocus {
  readonly entityType: string;
  readonly entityId: string;
  readonly title: string;
}

export const shellKeys = {
  meta: ['meta'] as const,
  /** Shared with Settings → Backups: invalidating it refreshes the sidebar's "Backed up …" line. */
  backups: ['backups'] as const,
  issuers: ['issuers', 'shell'] as const,
  audit: (limit: number, focus?: { entityType: string; entityId: string } | null) =>
    ['audit', { limit, entityType: focus?.entityType ?? null, entityId: focus?.entityId ?? null }] as const,
};

export function useMeta() {
  return useQuery({
    queryKey: shellKeys.meta,
    queryFn: () => api.get<AppMeta>('/meta'),
    staleTime: 60_000,
  });
}

export function useBackups() {
  return useQuery({
    queryKey: shellKeys.backups,
    queryFn: () => api.get<{ items: BackupFile[] }>('/backups'),
    select: (data) => data.items,
    staleTime: 60_000,
  });
}

export function useShellIssuers() {
  return useQuery({
    queryKey: shellKeys.issuers,
    queryFn: () => api.get<{ items: IssuerOption[] }>('/issuers'),
    select: (data) => data.items.filter((issuer) => !issuer.isArchived),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Newest audit entries; with a focus, that record's whole history is merged in. */
export function useAuditLog(limit = 50, enabled = true, focus: ActivityFocus | null = null) {
  return useQuery({
    queryKey: shellKeys.audit(limit, focus),
    queryFn: () =>
      api.get<{ items: AuditEntry[] }>('/audit', {
        limit,
        entity_type: focus?.entityType,
        entity_id: focus?.entityId,
      }),
    enabled,
    staleTime: 0,
  });
}
